import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { DatePipe, NgClass, NgTemplateOutlet } from '@angular/common';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    OnDestroy,
    OnInit,
    TemplateRef,
    ViewChild,
    ViewContainerRef,
    ViewEncapsulation,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButton, MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterLink } from '@angular/router';
import { NotificationsService } from 'app/layout/common/notifications/notifications.service';
import { Notification } from 'app/layout/common/notifications/notifications.types';
import { TimelineActivity } from 'app/layout/common/notifications/timeline-activity.types';
import { TimelineService } from 'app/layout/common/notifications/timeline.service';
import { Subject, takeUntil } from 'rxjs';

@Component({
    selector: 'notifications',
    templateUrl: './notifications.component.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    exportAs: 'notifications',
    imports: [
        MatButtonModule,
        MatIconModule,
        MatTooltipModule,
        NgClass,
        NgTemplateOutlet,
        RouterLink,
        DatePipe,
        MatTabsModule,
        FormsModule,
    ],
    standalone: true,
})
export class NotificationsComponent implements OnInit, OnDestroy {
    @ViewChild('notificationsOrigin') private _notificationsOrigin: MatButton;
    @ViewChild('notificationsPanel')
    private _notificationsPanel: TemplateRef<any>;

    notifications: Notification[];
    timelineActivities: TimelineActivity[] = [];
    activeTab: 'notifications' | 'timeline' = 'notifications';
    searchQuery: string = '';
    userId: string = '1'; // Default user ID, should be obtained from auth service
    unreadCount: number = 0;
    private _overlayRef: OverlayRef;
    private _unsubscribeAll: Subject<any> = new Subject<any>();

    /**
     * Constructor
     */
    constructor(
        private _changeDetectorRef: ChangeDetectorRef,
        private _notificationsService: NotificationsService,
        private _timelineService: TimelineService,
        private _overlay: Overlay,
        private _viewContainerRef: ViewContainerRef
    ) {}

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        // Subscribe to notification changes
        this._notificationsService.notifications$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((notifications: Notification[]) => {
                // Load the notifications
                this.notifications = notifications;

                // Calculate the unread count
                this._calculateUnreadCount();

                // Mark for check
                this._changeDetectorRef.markForCheck();
            });
            
        // Load timeline activities
        this.loadTimelineActivities();
        
        // Subscribe to timeline activity changes
        this._timelineService.activities$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((activities: TimelineActivity[]) => {
                // Load the timeline activities
                this.timelineActivities = activities;
                
                // Mark for check
                this._changeDetectorRef.markForCheck();
            });
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();

        // Dispose the overlay
        if (this._overlayRef) {
            this._overlayRef.dispose();
        }
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Open the notifications panel
     */
    openPanel(): void {
        // Return if the notifications panel or its origin is not defined
        if (!this._notificationsPanel || !this._notificationsOrigin) {
            return;
        }

        // Create the overlay if it doesn't exist
        if (!this._overlayRef) {
            this._createOverlay();
        }

        // Attach the portal to the overlay
        this._overlayRef.attach(
            new TemplatePortal(this._notificationsPanel, this._viewContainerRef)
        );
    }

    /**
     * Close the notifications panel
     */
    closePanel(): void {
        this._overlayRef.detach();
    }

    /**
     * Mark all notifications as read
     */
    markAllAsRead(): void {
        // Mark all as read
        this._notificationsService.markAllAsRead().subscribe();
    }

    /**
     * Toggle read status of the given notification
     */
    toggleRead(notification: Notification): void {
        // Toggle the read status
        notification.read = !notification.read;

        // Update the notification
        this._notificationsService
            .update(notification.id, notification)
            .subscribe();
    }

    /**
     * Delete the given notification
     */
    delete(notification: Notification): void {
        // Delete the notification
        this._notificationsService.delete(notification.id).subscribe();
    }

    /**
     * Load timeline activities
     */
    loadTimelineActivities(): void {
        this._timelineService.getTimelineAllActivity(this.userId).subscribe();
    }
    
    /**
     * Handle timeline search
     */
    handleTimelineSearch(): void {
        const searchObj = {
            userId: this.userId,
            searchQuery: this.searchQuery
        };
        
        if (this.searchQuery) {
            this._timelineService.getTimelineSearchActivity(searchObj).subscribe();
        } else {
            this.clearTimelineSearch();
        }
    }
    
    /**
     * Clear timeline search
     */
    clearTimelineSearch(): void {
        this.searchQuery = '';
        this._timelineService.clearSearch(this.userId).subscribe();
    }
    
    /**
     * Get CSS class for timeline activity based on action type
     */
    getTimelineCircleClass(action: string): string {
        if (action === 'marked_as_with_issues' || action === 'order_incomplete') {
            return 'circle-red';
        } else if (action === 'order_complete' || action === 'marked_as_completed' || action === 'invoice_paid' || action === 'order_delivered' || action === 'merchandise_delivered' || action === 'order_picked_up' || action === 'trader_received') {
            return 'circle-green';
        } else if (action === 'order_created' || action === 'apparel_ordered' || action === 'film_uploaded' || 
                  action === 'apparel_received' || action === 'apparel_purchased' || action === 'merchandise_picked_up' ||
                  action === 'photo_sample_sent' || action === 'photo_sample_uploaded' || action === 'photo_sample_reviewed') {
            return 'circle-blue';
        } else if (action === 'order_shipped' || action === 'shipping_out_for_delivery' || action === 'merchandise_tracking_status_updated' || action === 'merchandise_in_transit' || action === 'merchandise_out_for_delivery' || action === 'local_delivery_preparing' || action === 'returned_to_creator') {
            return 'circle-yellow';
        } else if (action === 'merchandise_returned') {
            return 'circle-red';
        } else {
            return 'circle-gray';
        }
    }
    
    /**
     * Format order status message
     */
    getOrderStatus(action: string, title: string): string {
        // Extract order number from title
        const orderId = title.replace(/[^0-9]/g, "");
        
        const actionMap = {
            'order_created': `New Order #${orderId} is created`,
            'moved_to_production': `#${orderId} in Production`,
            'order_deleted': `Order #${orderId} is Delete`,
            'order_incomplete': `Order #${orderId} is Incomplete`,
            'order_completed': `Order #${orderId} is Completed`,
            'order_shipped': `Order #${orderId} is Shipped`,
            'shipping_out_for_delivery': `Order #${orderId} is Out for Delivery`,
            'local_delivery_preparing': `Order #${orderId} is Preparing for Local Delivery`,
            'marked_as_with_issues': `Marked as issue #${orderId}`,
            'marked_as_completed': `Marked as Completed #${orderId}`,
            'marked_as_paid': `Marked as Paid #${orderId}`,
            'apparel_ordered': `Apparel Ordered for #${orderId}`,
            'film_uploaded': `Film Uploaded for #${orderId}`,
            'apparel_received': `Apparel Received for #${orderId}`,
            'printed_film': `Printed Film for #${orderId}`,
            'apparel_purchased': `Apparel Purchased for #${orderId}`,
            'invoice_paid': `Invoice #${orderId} is paid`,
            'invoice_created': `Invoice #${orderId} is created`,
            'order_delivered': `Order Delivered #${orderId}`,
            'order_picked_up': `Order #${orderId} is Picked Up`,
            'merchandise_picked_up': `Merchandise Picked Up for #${orderId}`,
            'merchandise_tracking_status_updated': `Merchandise Shipping Updated for #${orderId}`,
            'merchandise_in_transit': `Merchandise for #${orderId} is In Transit`,
            'merchandise_out_for_delivery': `Merchandise for #${orderId} is Out for Delivery`,
            'merchandise_delivered': `Merchandise Delivered for #${orderId}`,
            'merchandise_returned': `Merchandise for #${orderId} is Returning to Sender`,
            'photo_sample_sent': `Sample Photos Emailed for #${orderId}`,
            'photo_sample_uploaded': `Sample Photos Uploaded for #${orderId}`,
            'photo_sample_reviewed': `Sample Photos Reviewed for #${orderId}`,
            'resource_work_done': `Resource Work Done for #${orderId}`,
            'received_at_dp': `Order #${orderId} Received at DP`,
            'returned_to_creator': `Order #${orderId} Returned to Creator`,
            'handed_off': `Order #${orderId} Handed Off`,
            'trader_received': `Order #${orderId} Received by Creator`
        };
        
        return actionMap[action] || action;
    }

    /**
     * Track by function for ngFor loops
     *
     * @param index
     * @param item
     */
    trackByFn(index: number, item: any): any {
        return item.id || index;
    }
    
    /**
     * Open delivery photo in a new tab
     */
    openDeliveryPhoto(photoUrl: string): void {
        if (photoUrl) {
            window.open(photoUrl, '_blank');
        }
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Private methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Create the overlay
     */
    private _createOverlay(): void {
        // Create the overlay
        this._overlayRef = this._overlay.create({
            hasBackdrop: true,
            backdropClass: 'fuse-backdrop-on-mobile',
            scrollStrategy: this._overlay.scrollStrategies.block(),
            positionStrategy: this._overlay
                .position()
                .flexibleConnectedTo(
                    this._notificationsOrigin._elementRef.nativeElement
                )
                .withLockedPosition(true)
                .withPush(true)
                .withPositions([
                    {
                        originX: 'start',
                        originY: 'bottom',
                        overlayX: 'start',
                        overlayY: 'top',
                    },
                    {
                        originX: 'start',
                        originY: 'top',
                        overlayX: 'start',
                        overlayY: 'bottom',
                    },
                    {
                        originX: 'end',
                        originY: 'bottom',
                        overlayX: 'end',
                        overlayY: 'top',
                    },
                    {
                        originX: 'end',
                        originY: 'top',
                        overlayX: 'end',
                        overlayY: 'bottom',
                    },
                ]),
        });

        // Detach the overlay from the portal on backdrop click
        this._overlayRef.backdropClick().subscribe(() => {
            this._overlayRef.detach();
        });
    }

    /**
     * Calculate the unread count
     *
     * @private
     */
    private _calculateUnreadCount(): void {
        let count = 0;

        if (this.notifications && this.notifications.length) {
            count = this.notifications.filter(
                (notification) => !notification.read
            ).length;
        }

        this.unreadCount = count;
    }
}